import { TriageRule, TriageData, RuleCondition, Comparator, TriageExplanation, ExplanationFactor, UrgencyLevel } from '@/types/triage';
import * as MTSEngine from '@/utils/manchesterTriageSystem';

/**
 * Manchester Triage System Rules
 * 
 * Structured representation of clinical decision rules based on the 
 * Manchester Triage System, a globally recognized emergency triage method.
 * 
 * Each rule has a unique ID, clinical parameters it evaluates, and
 * human-readable explanations to ensure transparency.
 */
const manchesterRules: TriageRule[] = [
  // IMMEDIATE (RED) - Highest priority - Target time 0 minutes
  {
    id: 'MTS-RED-RESP-01',
    name: 'Compromised Airway',
    description: 'Patient shows signs of compromised airway function',
    category: 'vital',
    condition: {
      parameter: 'respiratory_distress',
      comparator: 'eq',
      value: true
    },
    impact: {
      urgency: 'immediate',
      description: 'Airway compromise requires immediate medical intervention',
      reference: 'MTS v3.0, Sec 2.1.1'
    }
  },
  {
    id: 'MTS-RED-CIRC-01',
    name: 'Severe Shock',
    description: 'Signs of severe shock with abnormal vital signs',
    category: 'vital',
    condition: {
      parameter: 'vitals.heartRate',
      comparator: 'gt',
      value: 130
    },
    impact: {
      urgency: 'immediate',
      description: 'Elevated heart rate indicating possible shock state',
      reference: 'MTS v3.0, Sec 2.2.3'
    }
  },
  {
    id: 'MTS-RED-CIRC-02',
    name: 'Severe Shock - Blood Pressure',
    description: 'Signs of severe shock with low blood pressure',
    category: 'vital',
    condition: {
      parameter: 'vitals.bloodPressure.systolic',
      comparator: 'lt',
      value: 90
    },
    impact: {
      urgency: 'immediate',
      description: 'Hypotension indicating possible shock state',
      reference: 'MTS v3.0, Sec 2.2.4'
    }
  },
  {
    id: 'MTS-RED-NEURO-01',
    name: 'Altered Consciousness',
    description: 'Significant alteration in consciousness level',
    category: 'vital',
    condition: {
      parameter: 'altered_consciousness',
      comparator: 'eq',
      value: true
    },
    impact: {
      urgency: 'immediate',
      description: 'Altered level of consciousness requiring immediate assessment',
      reference: 'MTS v3.0, Sec 2.3.1'
    }
  },
  {
    id: 'MTS-RED-CHEST-01',
    name: 'Severe Chest Pain',
    description: 'Chest pain with high-risk features',
    category: 'symptom',
    condition: {
      parameter: 'symptoms',
      comparator: 'includes',
      value: 'chest pain'
    },
    impact: {
      urgency: 'immediate',
      description: 'Acute chest pain suggesting possible cardiac emergency',
      reference: 'MTS v3.0, Sec 3.4.2'
    }
  },
  
  // VERY URGENT (ORANGE) - Target time 10 minutes
  {
    id: 'MTS-ORANGE-RESP-01',
    name: 'Moderate Respiratory Distress',
    description: 'Moderate difficulty breathing requiring prompt attention',
    category: 'vital',
    condition: {
      parameter: 'vitals.respiratoryRate',
      comparator: 'gt',
      value: 24
    },
    impact: {
      urgency: 'urgent',
      description: 'Elevated respiratory rate requiring prompt assessment',
      reference: 'MTS v3.0, Sec 2.1.3'
    }
  },
  {
    id: 'MTS-ORANGE-TEMP-01',
    name: 'High Fever',
    description: 'Significantly elevated body temperature',
    category: 'vital',
    condition: {
      parameter: 'vitals.temperature',
      comparator: 'gt',
      value: 102.2
    },
    impact: {
      urgency: 'urgent',
      description: 'High fever requiring prompt clinical assessment',
      reference: 'MTS v3.0, Sec 2.5.2'
    }
  },
  {
    id: 'MTS-ORANGE-NEURO-01',
    name: 'Severe Pain',
    description: 'Patient reporting severe pain (7-10/10)',
    category: 'symptom',
    condition: {
      parameter: 'severity',
      comparator: 'eq',
      value: 'severe'
    },
    impact: {
      urgency: 'urgent',
      description: 'Severe pain requiring prompt assessment and management',
      reference: 'MTS v3.0, Sec 3.1.2'
    }
  },
  {
    id: 'MTS-ORANGE-ACUTE-01',
    name: 'Acute Abdominal Pain',
    description: 'Acute abdominal pain with potential serious cause',
    category: 'symptom',
    condition: {
      parameter: 'symptoms',
      comparator: 'includes',
      value: 'abdominal pain'
    },
    impact: {
      urgency: 'urgent',
      description: 'Acute abdominal pain requiring prompt surgical assessment',
      reference: 'MTS v3.0, Sec 3.5.1'
    }
  },
  
  // URGENT (YELLOW) - Target time 60 minutes
  {
    id: 'MTS-YELLOW-RESP-01',
    name: 'Mild Respiratory Issue',
    description: 'Mild respiratory symptoms requiring assessment',
    category: 'symptom',
    condition: {
      parameter: 'symptoms',
      comparator: 'includes',
      value: 'cough'
    },
    impact: {
      urgency: 'standard',
      description: 'Respiratory symptoms requiring clinical assessment',
      reference: 'MTS v3.0, Sec 3.2.1'
    }
  },
  {
    id: 'MTS-YELLOW-TEMP-01',
    name: 'Moderate Fever',
    description: 'Moderately elevated body temperature',
    category: 'vital',
    condition: {
      parameter: 'vitals.temperature',
      comparator: 'gt',
      value: 100.4
    },
    impact: {
      urgency: 'standard',
      description: 'Fever requiring clinical assessment',
      reference: 'MTS v3.0, Sec 2.5.3'
    }
  },
  {
    id: 'MTS-YELLOW-PAIN-01',
    name: 'Moderate Pain',
    description: 'Patient reporting moderate pain (4-6/10)',
    category: 'symptom',
    condition: {
      parameter: 'severity',
      comparator: 'eq',
      value: 'moderate'
    },
    impact: {
      urgency: 'standard',
      description: 'Moderate pain requiring assessment',
      reference: 'MTS v3.0, Sec 3.1.3'
    }
  },
  
  // STANDARD (GREEN) - Target time 120 minutes
  {
    id: 'MTS-GREEN-MINOR-01',
    name: 'Minor Symptoms',
    description: 'Minor symptoms suitable for routine care',
    category: 'symptom',
    condition: {
      parameter: 'severity',
      comparator: 'eq',
      value: 'mild'
    },
    impact: {
      urgency: 'non-urgent',
      description: 'Minor symptoms suitable for routine assessment',
      reference: 'MTS v3.0, Sec 3.1.4'
    }
  },
  {
    id: 'MTS-GREEN-RECENT-01',
    name: 'Recent Minor Issue',
    description: 'Recent onset of minor issue',
    category: 'symptom',
    condition: {
      parameter: 'onset',
      comparator: 'eq',
      value: 'recent'
    },
    impact: {
      urgency: 'non-urgent',
      description: 'Recent onset of minor symptoms',
      reference: 'MTS v3.0, Sec 3.8.2'
    }
  }
];

/**
 * Get the value of a nested property from an object using a string path
 * @param obj The object to get the property from
 * @param path The path to the property, e.g. 'vitals.temperature'
 */
function getNestedProperty(obj: any, path: string): any {
  return path.split('.').reduce((prev, curr) => {
    return prev && prev[curr] !== undefined ? prev[curr] : undefined;
  }, obj);
}

/**
 * Evaluate a Manchester Triage rule against patient data
 * @param rule The rule to evaluate
 * @param triageData The patient's triage data
 * @returns true if the rule conditions are met, false otherwise
 */
/**
 * Helper function to safely get the parameter from a condition that might be an array
 */
function getConditionParameter(condition: RuleCondition | RuleCondition[]): string {
  if (Array.isArray(condition)) {
    throw new Error('Cannot get parameter directly from condition array');
  }
  return condition.parameter;
}

/**
 * Helper function to safely get the comparator from a condition that might be an array
 */
function getConditionComparator(condition: RuleCondition | RuleCondition[]): Comparator {
  if (Array.isArray(condition)) {
    throw new Error('Cannot get comparator directly from condition array');
  }
  return condition.comparator;
}

/**
 * Helper function to safely get the value from a condition that might be an array
 */
function getConditionValue(condition: RuleCondition | RuleCondition[]): string | number | boolean | string[] {
  if (Array.isArray(condition)) {
    throw new Error('Cannot get value directly from condition array');
  }
  return condition.value;
}

/**
 * Helper function to safely get all properties from a condition
 */
function getConditionProperties(condition: RuleCondition | RuleCondition[]): RuleCondition {
  if (Array.isArray(condition)) {
    throw new Error('Cannot get properties directly from condition array');
  }
  return condition;
}

/**
 * Helper function to evaluate a single condition
 */
function evaluateSingleCondition(
  parameter: string,
  comparator: Comparator,
  value: string | number | boolean | string[],
  triageData: TriageData
): boolean {
  // Get the value to compare
  let actualValue = null;
  
  // Extract the value from the triage data based on the parameter path
  const paramParts = parameter.split('.');
  let currentObj: any = triageData;
  
  for (const part of paramParts) {
    if (currentObj === undefined || currentObj === null) {
      return false;
    }
    currentObj = currentObj[part];
  }
  
  actualValue = currentObj;
  
  // Return false if value is undefined
  if (actualValue === undefined || actualValue === null) {
    return false;
  }
  
  // Compare based on the comparator
  switch (comparator) {
    case 'eq':
      return actualValue === value;
    case 'neq':
      return actualValue !== value;
    case 'gt':
      return actualValue > value;
    case 'gte':
      return actualValue >= value;
    case 'lt':
      return actualValue < value;
    case 'lte':
      return actualValue <= value;
    case 'includes':
      if (Array.isArray(actualValue)) {
        return actualValue.some(v => 
          v.toLowerCase().includes((value as string).toLowerCase())
        );
      } else if (typeof actualValue === 'string') {
        return actualValue.toLowerCase().includes((value as string).toLowerCase());
      }
      return false;
    case 'contains':
    case 'contains_any':
      if (Array.isArray(actualValue) && Array.isArray(value)) {
        return actualValue.some(av => value.includes(av));
      } else if (Array.isArray(actualValue) && typeof value === 'string') {
        return actualValue.some(av => av.includes(value as string));
      } else if (typeof actualValue === 'string' && Array.isArray(value)) {
        return value.some(v => actualValue.includes(v as string));
      } else if (typeof actualValue === 'string' && typeof value === 'string') {
        return actualValue.includes(value);
      }
      return false;
    default:
      return false;
  }
}

function evaluateRule(rule: TriageRule, triageData: TriageData): boolean {
  // Handle compound rules with multiple conditions
  if (Array.isArray(rule.condition)) {
    // All conditions must be true for a compound rule to match
    for (const condition of rule.condition) {
      const { parameter, comparator, value } = getConditionProperties(condition);
      if (!evaluateSingleCondition(parameter, comparator, value, triageData)) {
        return false;
      }
    }
    return true; // All conditions passed
  }
  
  // Handle single condition rules
  const { parameter, comparator, value } = getConditionProperties(rule.condition);
  
  // Get the value to compare
  let actualValue = null;
  
  if (parameter === 'symptoms') {
    if (comparator === 'includes') {
      return triageData.symptoms.some(s => 
        s.toLowerCase().includes((value as string).toLowerCase())
      );
    } else {
      return false; // Other comparators not applicable to symptom arrays
    }
  } else if (parameter === 'respiratory_distress' || parameter === 'altered_consciousness') {
    // These are special parameters that would be derived from symptoms or additional info
    // For now, check if mentioned in symptoms or additionalInfo
    const combinedText = [
      ...triageData.symptoms, 
      triageData.additionalInfo
    ].join(' ').toLowerCase();
    
    if (parameter === 'respiratory_distress') {
      return combinedText.includes('breathing') || 
             combinedText.includes('breath') || 
             combinedText.includes('respir');
    } else { // altered_consciousness
      return combinedText.includes('confused') || 
             combinedText.includes('consciousness') ||
             combinedText.includes('alert') ||
             combinedText.includes('responsive');
    }
  } else {
    // For any other parameter, get the nested property
    actualValue = getNestedProperty(triageData, parameter);
    
    // Handle cases where we need to extract from objects like bloodPressure
    if (parameter === 'vitals.bloodPressure' && typeof actualValue === 'string') {
      // Try to extract systolic from format like "120/80"
      const match = actualValue.match(/^(\d+)/);
      if (match && match[1]) {
        // If we're looking for systolic specifically
        if (getConditionParameter(rule.condition).includes('systolic')) {
          actualValue = parseInt(match[1], 10);
        }
      }
    }
  }
  
  // Return false if value is undefined
  if (actualValue === undefined || actualValue === null) {
    return false;
  }
  
  // Compare based on the comparator
  switch (comparator) {
    case 'eq':
      return actualValue === value;
    case 'gt':
      return actualValue > value;
    case 'lt':
      return actualValue < value;
    case 'gte':
      return actualValue >= value;
    case 'lte':
      return actualValue <= value;
    case 'includes':
      if (Array.isArray(actualValue)) {
        return actualValue.some(v => v === value || 
          (typeof v === 'string' && typeof value === 'string' && 
           v.toLowerCase().includes(value.toLowerCase())));
      }
      return false;
    case 'excludes':
      if (Array.isArray(actualValue)) {
        return !actualValue.some(v => v === value || 
          (typeof v === 'string' && typeof value === 'string' && 
           v.toLowerCase().includes(value.toLowerCase())));
      }
      return true;
    default:
      return false;
  }
}

/**
 * Transform matched rules into explanation factors for transparent AI
 * @param matchedRules Array of rules that matched the patient's triage data
 * @param triageData The patient's triage data
 * @returns Array of explanation factors for transparency
 */
function generateExplanationFactors(matchedRules: TriageRule[], triageData: TriageData): ExplanationFactor[] {
  const factors: ExplanationFactor[] = [];
  
  // Add factors from each matched rule
  matchedRules.forEach(rule => {
    const { parameter, comparator, value } = getConditionProperties(rule.condition);
    
    let displayValue: string | number | boolean | string[] = value;
    let actualValue: any = null;
    
    if (parameter === 'symptoms') {
      // Find the matching symptom
      const matchedSymptom = triageData.symptoms.find(s => 
        s.toLowerCase().includes((value as string).toLowerCase())
      );
      if (matchedSymptom) {
        displayValue = matchedSymptom;
      }
    } else if (parameter !== 'respiratory_distress' && parameter !== 'altered_consciousness') {
      // Get the actual value for other parameters
      actualValue = getNestedProperty(triageData, parameter);
      if (actualValue !== undefined) {
        displayValue = actualValue;
      }
    }
    
    // Set impact level based on rule urgency
    const impactLevel = rule.impact.urgency === 'immediate' ? 'high' :
                      rule.impact.urgency === 'urgent' ? 'medium' : 'low';
    
    factors.push({
      parameter: rule.name,
      value: displayValue,
      impact: impactLevel,
      description: rule.impact.description
    });
  });
  
  // Add general factors if no specific factors were found
  if (factors.length === 0) {
    // Check common vital signs
    if (triageData.vitals?.temperature && triageData.vitals.temperature > 99.5) { // Temperature is now a number
      factors.push({
        parameter: 'Elevated Temperature',
        value: triageData.vitals.temperature.toString(), // Convert number to string for display
        impact: 'low',
        description: 'Slightly elevated temperature noted'
      });
    }
    
    // Add general symptom factor
    if (triageData.symptoms.length > 0) {
      factors.push({
        parameter: 'Reported Symptoms',
        value: triageData.symptoms.join(', '),
        impact: 'low',
        description: 'Patient-reported symptoms considered in assessment'
      });
    }
    
    // Add severity factor
    factors.push({
      parameter: 'Reported Severity',
      value: triageData.severity,
      impact: 'low',
      description: 'Patient-reported severity level'
    });
  }
  
  return factors;
}

/**
 * Calculate confidence score for the triage decision
 * @param matchedRules Array of rules that matched the patient's data
 * @param triageData The patient's triage data
 * @returns Confidence score between 0.0 and 1.0
 */
function calculateConfidence(matchedRules: TriageRule[], triageData: TriageData): number {
  // Base confidence starts at 0.7 (70%)
  let confidence = 0.7;
  
  // Increase confidence with more matched rules
  confidence += Math.min(matchedRules.length * 0.05, 0.15);
  
  // Increase confidence if vital signs are present
  if (triageData.vitals.temperature) confidence += 0.03;
  if (triageData.vitals.heartRate) confidence += 0.03;
  if (triageData.vitals.bloodPressure) confidence += 0.03;
  
  // Higher confidence for more severe categories
  const highestUrgency = matchedRules.reduce((highest, rule) => {
    const urgencyValue = 
      rule.impact.urgency === 'immediate' ? 4 :
      rule.impact.urgency === 'urgent' ? 3 :
      rule.impact.urgency === 'standard' ? 2 : 1;
    
    return Math.max(highest, urgencyValue);
  }, 0);
  
  confidence += (highestUrgency * 0.01);
  
  // Decrease confidence if multiple different urgency levels matched
  const uniqueUrgencies = new Set(matchedRules.map(r => r.impact.urgency));
  if (uniqueUrgencies.size > 1) {
    confidence -= (uniqueUrgencies.size - 1) * 0.05;
  }
  
  // Ensure confidence stays within 0.0-1.0 range
  return Math.max(0.0, Math.min(1.0, confidence));
}

/**
 * Generate a human-readable explanation of the triage decision
 * Main entry point for the Glass Box AI Triage System
 * 
 * This function serves as the primary interface for triage explanation generation.
 * It now leverages the comprehensive Manchester Triage System implementation
 * while maintaining backward compatibility with existing components.
 * 
 * @param triageData The patient's triage data
 * @returns TriageExplanation object with urgency, confidence, and factors
 */
export function explainTriage(triageData: TriageData): TriageExplanation {
  try {
    console.log('[MTRules Debug] explainTriage called with triageData:', JSON.stringify({
      symptoms: triageData.symptoms,
      severity: triageData.severity,
      duration: triageData.duration,
      vitals: triageData.vitals,
      respiratory_distress: triageData.respiratory_distress
    }, null, 2));
    
    // Use the comprehensive MTS engine implementation
    const comprehensiveExplanation = MTSEngine.explainTriage(triageData);
    
    console.log('[MTRules Debug] MTSEngine.explainTriage returned:', JSON.stringify({
      urgency: comprehensiveExplanation.urgency,
      confidence: comprehensiveExplanation.confidence,
      rules: comprehensiveExplanation.rules,
      presentingComplaint: comprehensiveExplanation.presentingComplaint,
      discriminator: comprehensiveExplanation.discriminator
    }, null, 2));
    
    if (comprehensiveExplanation.rules.length > 0) {
      // The comprehensive system provides presentingComplaint and discriminator
      // which we need to ensure backward compatibility with existing components
      return {
        urgency: comprehensiveExplanation.urgency,
        confidence: comprehensiveExplanation.confidence,
        factors: comprehensiveExplanation.factors,
        rules: comprehensiveExplanation.rules,
        presentingComplaint: comprehensiveExplanation.presentingComplaint,
        discriminator: comprehensiveExplanation.discriminator
      };
    }

    // The following is the legacy/fallback path. It appears to be broken (references undefined 'findRuleById', 'manchesterRules', etc.)
    // and likely unused if comprehensiveExplanation.rules.length > 0. Commenting it out entirely.
    /*
    console.log('[MTRules Debug] comprehensiveExplanation has no rules, attempting legacy fallback.');
    // Legacy implementation as fallback for backward compatibility
    // Filter rules that match the patient's data
    const matchedRules = manchesterRules.filter(rule => 
      evaluateRule(rule, triageData)
    );
    
    // Find the highest urgency level from matched rules
    let highestUrgency: UrgencyLevel = 'non-urgent';
    let urgencyValue = 0;
    
    matchedRules.forEach(rule => {
      const currentUrgencyValue = 
        rule.impact.urgency === 'immediate' ? 4 :
        rule.impact.urgency === 'urgent' ? 3 :
        rule.impact.urgency === 'standard' ? 2 : 1;
      
      if (currentUrgencyValue > urgencyValue) {
        urgencyValue = currentUrgencyValue;
        highestUrgency = rule.impact.urgency as UrgencyLevel;
      }
    });
    
    // Generate explanation factors
    const factors = generateExplanationFactors(matchedRules, triageData);
    
    // Calculate confidence score
    const localConfidence = calculateConfidence(matchedRules, triageData);
    // console.log(`[MTRules Debug] Legacy path local calculateConfidence returned: ${localConfidence.toFixed(3)}`);

    // Return the explanation with backward compatibility
    return {
      urgency: highestUrgency,
      confidence: localConfidence,
      factors,
      rules: matchedRules.map(r => r.id)
    };
    */

    // If comprehensiveExplanation had no rules and fallback is commented out, we must return something.
    // This case should ideally not be hit if MTSEngine is robust.
    // For safety, return a more meaningful fallback explanation based on symptoms
    console.warn('[MTRules Debug] No rules from comprehensiveExplanation. Using improved fallback explanation.');
    
    // Extract the primary symptom for better presentation
    const primarySymptom = triageData.symptoms && triageData.symptoms.length > 0 ? 
      triageData.symptoms[0] : 'Unspecified symptoms';
    
    // Determine a reasonable urgency based on available data
    let fallbackUrgency: UrgencyLevel = 'standard'; // Default to standard as safer than non-urgent
    let fallbackConfidence = 0.4; // Low but not very low confidence
    
    // Check for any critical values that might suggest higher urgency
    if (triageData.respiratory_distress) {
      fallbackUrgency = 'urgent';
      fallbackConfidence = 0.6;
    } else if (triageData.vitals?.temperature && triageData.vitals.temperature > 38.5) {
      fallbackUrgency = 'urgent';
      fallbackConfidence = 0.5;
    }
    
    return {
      urgency: fallbackUrgency,
      confidence: fallbackConfidence,
      factors: [{ 
        parameter: 'clinical assessment', 
        value: 'limited data', 
        impact: 'medium', 
        description: 'Assessment based on limited clinical data. Consider manual review.' 
      }],
      rules: [],
      presentingComplaint: primarySymptom,
      discriminator: 'Clinical Assessment' // Fallback discriminator
    };
  } catch (error) {
    const err = error as Error;
    // console.log(`[MTRules Debug] Calling MTSEngine.explainTriage with TriageData:`, JSON.stringify(triageData, null, 2).substring(0, 500));
    // console.error('[MTRules Debug] Error caught in explainTriage:', err.message, err.stack ? err.stack.substring(0, 500) : 'No stack trace');
    // console.error('Error in triage explanation generation (full):', err); // Keep this commented unless very verbose logging is needed.
    
    // Return fallback explanation
    return {
      urgency: 'standard', // Default to standard as a safe fallback
      confidence: 0.5, // Medium confidence
      factors: [{
        parameter: 'System Assessment',
        value: 'Fallback triggered',
        impact: 'medium',
        description: 'Explanation unavailable—defaulting to standard triage protocol'
      }],
      rules: []
    };
  }
}
